import { Component, OnInit, ViewChild, HostListener, TemplateRef, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { environment } from '../../../../environments/environment';

import { MatterSearchComponent } from '../../common/matter-search/matter-search.component';

import { AuthService } from '../../../services/common/auth.service';
import { ActionCableService } from '../../../services/common/action-cable.service';
import { HttpClientService } from '../../../services/common/http-client.service';
import { NotyService } from '../../../services/common/noty.service';
import { ScansService } from '../../../services/scans/scans.service';


@Component({
  selector: 'app-scan-index',
  templateUrl: './scan-index.component.html',
  styleUrls: ['./scan-index.component.scss']
})
export class ScanIndexComponent implements OnInit, OnDestroy {
  deleting:             boolean    = false;
  deletingAll:          boolean    = false;
  deleteWarningMessage: string;
  scans:                Array<any> = [];
  searchOptions:        any        = {};
  searching:            boolean    = false;
  sidebarMaximized:     boolean    = false;
  sidebarOpen:          boolean    = false;
  testCleanupEnabled:   boolean    = false;
  componentDestroyed:   boolean    = false;
  maxCount:             number;
  pendingCount:         number;
  openCount:            number;
  filterCounts:         any        = {};
  sidebarScrollTimeout: any;
  scanQueueChannelSubscription: any;
  scanQueueRefreshTimeout: any;
  /**
   * Search params for search request
   * @type {any}
   */
   searchParams: any = {
    state:    "pending",
    priority: "",
    doc_type: "",
    matter_id_field_search_results: null,
    page:     1,
    per_page: 5,
  };
  /**
   * Subscription to router events to catch page changes and matter id changes in the URL
   * @type {Subscription}
   */
  routerEventsSubscription: Subscription;
  /**
   * Matter search component
   * @param {[type]} 'matterSearch'
   */
  @ViewChild('matterSearch') matterSearchComponent: MatterSearchComponent;

  @ViewChild('confirmDeleteModal', { static: true }) confirmDeleteModalTemplateRef: TemplateRef<any>;

  constructor(private route:             ActivatedRoute,
              private router:            Router,
              private actionCableService: ActionCableService,
              private authService:       AuthService,
              private httpClientService: HttpClientService,
              private ngbModalService:   NgbModal,
              private notyService:       NotyService,
              private scansService:      ScansService) {
    this.routerEventsSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      if(this.componentDestroyed) return;
      this.initialize();
    });
  }


  ngOnInit(){
    this.initialize();
    this.subscribeScanQueueChannel();
  }

  ngAfterViewInit() {
    this.sidebarScrollTimeout = setTimeout(() => {
      if(this.componentDestroyed) return;
      this.content_sidebar_scroll();
    }, 500);
  }

  /**
   * Called when component is destroyed
   */
  ngOnDestroy() {
    this.componentDestroyed = true;
    if(this.routerEventsSubscription) this.routerEventsSubscription.unsubscribe();
    if(this.sidebarScrollTimeout) clearTimeout(this.sidebarScrollTimeout);
    if(this.scanQueueRefreshTimeout) clearTimeout(this.scanQueueRefreshTimeout);
    if(this.scanQueueChannelSubscription) this.scanQueueChannelSubscription.unsubscribe();
  }

  @HostListener('window:scroll', ['$event'])
  window_scroll(event: any) {
    this.content_sidebar_scroll();
  }

  @HostListener('window:resize', ['$event'])
  window_resize(event: any) {
    this.content_sidebar_scroll();
  }

  clearSearch = () => {
    if(this.componentDestroyed) return;
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: {} });
    this.resetParams();
    this.search();
  }

  deleteScanConfirm(scan: any) {
    this.deleteWarningMessage = `
      <div class="text-center">
        <strong>Delete scan <em>${scan.id}</em>?</strong>
        <br />
        <img class="img-thumbnail w-50" src="${scan.png_thumbnail_src}" class="card-img-top" alt="..."></a>      
        <br/>
        ${scan.scan_info}
      </div>`;
    this.ngbModalService.open(this.confirmDeleteModalTemplateRef).result.then(
      (result) => {  
        this.deleting = true;    
        //TODO: use response_json.success to verify scan deletion after backend is changed to match it
        this.scansService.delete({id: scan.id}).then(response_json => {
          if(this.componentDestroyed) return;
          this.deleting = false;
          if(response_json.resubmitted_count) {
            this.notyService.notify("Scan deleted and email returned to intake","success")
          } else {
            this.notyService.notify("Scan deleted","success")
          }
          this.search();
        }).catch(error => {          
          if(this.componentDestroyed) return;
          this.deleting = false;
          console.error('Scan delete error', error);
          this.notyService.notify("Error deleting scan","error")
        })   
        this.deleteWarningMessage = '';
      },
      (reason) => {
        // Cancelled
        this.deleteWarningMessage = '';
      }
    );
  }

  deleteAllScansConfirm() {
    const count = this.openCount || 0;
    if(!count) {
      this.notyService.notify("There are no open scans to delete", "info");
      return;
    }

    this.deleteWarningMessage = `
      <div class="text-center">
        <strong>Delete all ${count} open scan${count === 1 ? '' : 's'}?</strong>
        <br />
        This removes pending, processing, scanner-review, and attorney-review scans from the queue, along with their linked histories.
      </div>`;
    this.ngbModalService.open(this.confirmDeleteModalTemplateRef).result.then(
      (result) => {
        this.deletingAll = true;
        this.scansService.bulkDelete({ expected_count: count }).then(response_json => {
          if(this.componentDestroyed) return;
          this.deletingAll = false;
          const destroyedCount = response_json.destroyed_count || 0;
          if(response_json.success === false) {
            const errorMessage = response_json.errors && response_json.errors.length ? response_json.errors.join("<br />") : "No scans were deleted";
            this.notyService.notify(errorMessage, "warning");
          } else {
            this.notyService.notify(`Deleted ${destroyedCount} open scan${destroyedCount === 1 ? '' : 's'}`, "success");
          }
          this.search();
        }).catch(error => {
          if(this.componentDestroyed) return;
          this.deletingAll = false;
          console.error('Bulk scan delete error', error);
          this.notyService.notify("Error deleting scans", "error");
        })
        this.deleteWarningMessage = '';
      },
      (reason) => {
        this.deleteWarningMessage = '';
      }
    );
  }

  initialize() {
    if(this.componentDestroyed) return;
    this.resetParams();
    Object.assign(this.searchParams, this.route.snapshot.queryParams);
    this.scansService.index({search_options:true}).then(response_json=> { 
      if(this.componentDestroyed) return;
      this.searchOptions = response_json;

      if(this.searchOptions && this.searchOptions.per_page)
        this.searchParams.per_page = this.searchOptions.per_page;

      this.search();
    });
  }

  onMatterSelect(matter_id: number) {
   this.searchParams.matter_id_field_search_results = matter_id
  }

  activateIntranet(intranetId: number): void {
    if (!intranetId) return;

    const value = String(intranetId);
    window.sessionStorage.setItem('intranetId', value);
    window.localStorage.setItem('intranetId', value);
    document.cookie = `intranetId=${encodeURIComponent(value)}; path=/`;
  }

  subscribeScanQueueChannel(): void {
    this.actionCableService.cable().then((cable: any) => {
      if (this.componentDestroyed) return;

      this.scanQueueChannelSubscription = cable.subscriptions.create(
        { channel: 'ScanQueueChannel' },
        {
          received: (data: any) => {
            if (!data || data.action !== 'scan_queue_status') return;
            if (this.scanQueueRefreshTimeout) clearTimeout(this.scanQueueRefreshTimeout);
            this.scanQueueRefreshTimeout = setTimeout(() => {
              this.scanQueueRefreshTimeout = null;
              if (!this.componentDestroyed) this.search();
            }, 250);
          }
        }
      );
    });
  }

  resetParams() {    
    this.searchParams = {
      state: "pending",
      priority: null,
      doc_type: "",
      matter_id_field_search_results: null,
      page: 1,
      per_page: 50,
    }
  }

  search(pageEvent: any = null) {
    if(this.componentDestroyed) return;
    this.searching = true;

    if (pageEvent) this.searchParams.page = pageEvent.page;

    this.scansService.index(this.searchParams).then(response_json => {
      if(this.componentDestroyed) return;
      this.scans     = response_json.scans
      this.maxCount  = response_json.count;
      this.pendingCount = response_json.pending_count;
      this.openCount = response_json.open_count;
      this.filterCounts = response_json.filter_counts || {};
      this.testCleanupEnabled = !!response_json.scan_queue_test_cleanup_enabled;
      this.searching = false;

      // Add auth token to image URLs for authentication
      let baseUrl: string = '';
      if(environment.url && environment.url.indexOf(window.location.origin) < 0) {
        baseUrl = environment.url;
      }
      for(let scan of this.scans) {
        if(scan.png_thumbnail_src) {
          let fullUrl = baseUrl ? `${baseUrl}${scan.png_thumbnail_src}` : scan.png_thumbnail_src;
          scan.png_thumbnail_src = this.authService.getAuthenticatedDownloadUrl(fullUrl);
        }
      }
      let newPageUrl: string = this.httpClientService.buildNewPageUrl(this.scansService.url, this.searchParams);
      if(window.location.href != newPageUrl)
        this.httpClientService.setNewPageUrl(newPageUrl);
    }).catch(error => {
      if(this.componentDestroyed) return;
      this.searching = false;
      console.error("scans index failure response", error);
      this.notyService.notify('Error loading billings', 'error');
    });
  }

  content_sidebar_scroll() {
    if(this.componentDestroyed) return;
    var content_main = document.getElementById('content-main');
    var content_sidebar = document.getElementById('content-sidebar');
    var sidebar_height = window.innerHeight;
    if (content_sidebar && content_main) {
      if (content_main.getBoundingClientRect().top > 19) {
        content_sidebar.style.position = 'absolute';
        content_sidebar.style.top = content_main.offsetTop + 'px';
        sidebar_height -= content_main.getBoundingClientRect().top + 20;
        //console.log('greater ' + matter_sidebar.style.top);
      } else {
        content_sidebar.style.position = 'fixed';
        content_sidebar.style.top = '20px';
        sidebar_height -= content_sidebar.getBoundingClientRect().top + 20;
        //console.log('lesser ' + matter_sidebar.style.top);
      }

      if (content_sidebar.clientHeight != sidebar_height) content_sidebar.style.height = sidebar_height + 'px';
    }
  }
}
